import React, { useEffect, useState, useRef } from 'react'
import CustomReportsHeader from '../CustomReports/CustomReportsHeader'
import { useTranslation } from 'react-i18next'
import InfoButton from '../../common/InfoButton'
import { SearchOptionButtons } from '../AdvancedAnalyticsSearchWidget'
import { ReactComponent as SearchBlue } from '../../../assets/svg/SearchBlue.svg'
import { ReactComponent as UserCardId } from '../../../assets/svg/UserCardId.svg'
import { usePageTitle, useQuery } from '../../../utils/util'
import { searchClassForAnalytics, searchStudentForAnalytics, searchYearForAnalytics } from '../../../server/server'
import { Link, useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import { ReactComponent as ChevronDownIcon } from '../../../assets/svg/ChevronDownIcon.svg'
import Loading from '../../Loading'
import InfiniteScroll from 'react-infinite-scroll-component'
import { ReactComponent as CalendarIcon } from '../../../assets/svg/CalendarIcon.svg'
import { useUser } from '../../../auth'
import { updateYearAnalyticsAccess } from '../../../auth/yearAnalyticsAccessCache'

function AnalyticsSearch() {
    const { user } = useUser()
    usePageTitle('Upstrive Education - Analytics - Search')
    const { t } = useTranslation()
    const history = useHistory()
    const query = useQuery()

    const _searchText = query.get('search')
    const _type = query.get('type')

    const [searchText, setSearchText] = useState(_searchText || '')
    const [isFocused, setIsFocused] = useState(false)
    const [showSearchResults, setShowSearchResults] = useState(false)
    const [loading, setLoading] = useState(false)
    const [initLoading, setInitLoading] = useState(true)
    const [results, setResults] = useState([])
    const [hasMore, setHasMore] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)
    const [total, setTotal] = useState(0)
    const [selectedButton, setSelectedButton] = useState(['students', 'classes', 'years'].includes(_type) ? _type : 'students') // students | classes
    const studentsSelected = selectedButton === 'students'
    const yearsSelected = selectedButton === 'years'
    const requestVersion = useRef(0)
    const [error, setError] = useState('')

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchResults(searchText, 0)
        }, 400)
        return () => { clearTimeout(delayDebounceFn); requestVersion.current += 1 }
    }, [searchText, selectedButton])

    const loadMore = () => {
        if (!loadingMore && !loading) {
            setLoadingMore(true)
            fetchResults(searchText, results?.length)
        }
    }

    const fetchResults = async (searchTerm, resultsNum) => {
        const version = ++requestVersion.current
        setLoading(true)
        setError('')
        const data = {
            searchText: searchTerm.toLowerCase(),
            loadedData: resultsNum,
        }
        const callFunction = yearsSelected ? searchYearForAnalytics : studentsSelected ? searchStudentForAnalytics : searchClassForAnalytics
        const resp = await callFunction(data)
        if (version !== requestVersion.current) return
        if (yearsSelected) updateYearAnalyticsAccess(user, resp)
        if (resp.status === 200) {
            const array = resp?.data?.results
            const size = resp?.data?.total_count?.count
            if (array) {
                if (resultsNum === 0) {
                    setResults(array)
                } else {
                    setResults((prev) => [...prev, ...array])
                }
                setTotal(size)
                setHasMore(size > resultsNum + array.length)
            } else {
                setResults([])
                setTotal(0)
            }
            setShowSearchResults(true)
        } else {
            setResults([])
            setTotal(0)
            setHasMore(false)
            setShowSearchResults(true)
            setError(resp.status === 403 ? 'You do not have access to year analytics' : 'Unable to load analytics search')
        }
        setLoading(false)
        setLoadingMore(false)
        setInitLoading(false)
    }

    const changeText = (e) => {
        setSearchText(e.target.value)
        setShowSearchResults(true)
        setLoading(true)
        if (initLoading) {
            setInitLoading(false)
        }
    }

    useEffect(() => {
        if (!initLoading) {
            history.replace({
                search: `?search=${encodeURIComponent(searchText)}&type=${selectedButton}`,
            })
        }
    }, [searchText, selectedButton])

    const changeSelectedButton = (type) => {
        setSelectedButton(type)
        setShowSearchResults(false)
        setSearchText('')
        setLoading(true)
    }

    return (
        <div id="scroll-inf-search" className="analytics-container  light-small-scroll bg-[#ECEBF8] ">
            <div className="analytics-inner-container !mb-[10px] !h-auto ">
                <CustomReportsHeader title={t('Search All Analytics')} />
                <div className="flex flex-row gap-[20px] mt-[30px] flex-1 w-full ">
                    <div className="rounded-[10px] bg-[white] flex-1 flex flex-col px-[30px] py-[24px]">
                        <div className="flex flex-row items-center justify-between gap-[10px]">
                            <p className=" text-[20px] font-bold text-[#413F5E]">{t('Search All Analytics')}</p>
                            <InfoButton text={t('Use this search to find a student for deeper and advanced analytics')} />
                        </div>
                        <SearchOptionButtons selected={selectedButton} setSelected={changeSelectedButton} />
                        <div
                            className={`mt-[16px] flex flex-row items-center justify-center gap-[6px] border border-[#BEBECB] rounded-full h-[44px] min-h-[44px] px-[20px] bg-white relative transition-all`}
                        >
                            <input
                                type="text"
                                placeholder={yearsSelected ? t('Search for years') : studentsSelected ? t('Search for students') : t('Search for classes')}
                                className="flex-1 transition-all bg-[transparent] placeholder:text-[#9392AD] leading-5 text-[16px] text-deepPurple font-normal "
                                value={searchText}
                                id="analytics-search-input"
                                onChange={changeText}
                                onFocus={() => {
                                    setIsFocused(true)
                                }}
                                onBlur={() => {
                                    setIsFocused(false)
                                }}
                            />
                            {loading ? <Loading /> : <SearchBlue color="#5E639B" />}
                        </div>
                        {showSearchResults && (
                            <div className="flex flex-col gap-[16px] mt-[10px]">
                                <p className="px-[8px] text-[#413F5E] font-medium text-[14px]">
                                    {loading ? t('Loading') + '...' : <span>{yearsSelected ? t('We found {{total}} year(s)', { total }) : studentsSelected ? t('We found student(s)', { total }) : t('We found class(es)', { total })}</span>}
                                </p>

                                <InfiniteScroll
                                    id={`infinite-scroll-analytics-${selectedButton}`}
                                    dataLength={results.length}
                                    next={loadMore}
                                    hasMore={hasMore}
                                    className="flex flex-col gap-[8px] overflow-auto"
                                    scrollableTarget="scroll-inf-search"
                                >
                                    {error && <p role="alert" className="text-darkenPurple">{t(error)}</p>}
                                    {yearsSelected && !loading && !error && total === 0 && <p className="text-strongGray">{t(searchText ? 'No matching years' : 'No year groups assigned')}</p>}
                                    {results.map((e) => (yearsSelected ? <SingleYearButton year={e} key={e.id} /> : studentsSelected ? <SingleStudentButton student={e} key={e.id} /> : <SingleClassButton classData={e} key={e.id} />))}
                                </InfiniteScroll>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default AnalyticsSearch

const SingleStudentButton = ({ student }) => {
    const { t } = useTranslation()
    return (
        <div className="h-[48px] bg-[#F8F8FB] rounded-[8px] px-[16px] flex flex-row items-center gap-[16px]">
            <p className="text-[16px] text-[#413F5E] font-semibold">{student.label}</p>
            <p className="text-[#77768D] font-normal text-[15px]">{student.additionalInformation}</p>
            <div className="flex-1" />
            <Link to={`/analytics/student?id=${student.id}`}>
                <div className="flex flex-row items-center gap-[6px] bg-[#D8D9E9] h-[30px] px-[15px] rounded-full" id="student-analytics-link">
                    <p className="text-[#413F5E] font-bold text-[14px]">{t('Student analytics')}</p>
                    <UserCardId />
                </div>
            </Link>
        </div>
    )
}

const SingleClassButton = ({ classData }) => {
    const { t } = useTranslation()
    const [showMore, setShowMore] = useState(false)

    const students = classData.students

    return (
        <>
            <div className={`h-[48px]  rounded-[8px] px-[16px] flex flex-row items-center gap-[16px] transition-all ${showMore ? 'bg-[#ECEBF8]' : 'bg-[#F8F8FB]'} z-[1]`}>
                <p className="text-[16px] text-[#413F5E] font-semibold">{classData.label}</p>
                <p className="text-[#77768D] font-normal text-[15px]">{classData.additionalInformation}</p>
                <div className="flex-1" />
                <Link to={`/analytics/class?id=${classData.id}`} id="class-analytics-link">
                    <div className="flex flex-row items-center gap-[6px] bg-[#D8D9E9] h-[30px] px-[25px] rounded-full">
                        <p className="text-[#413F5E] font-bold text-[14px]">{t('Class analytics')}</p>
                        <UserCardId />
                    </div>
                </Link>
                <button onClick={() => setShowMore(!showMore)} className={`h-[32px] w-[32px] flex items-center justify-center ${showMore ? 'rotate-180' : ''}  transition-all`}>
                    <ChevronDownIcon />
                </button>
            </div>
            {showMore && (
                <div className="bg-[#F8F8FB] -mt-[20px] pt-[16px] rounded-[8px] pb-[10px]">
                    {students?.map((student, index) => (
                        <div
                            className={`h-[48px] bg-[#F8F8FB] rounded-[8px] px-[16px] flex flex-row items-center gap-[16px] mx-[8px] ${
                                index !== students.length - 1 ? 'border-b border-[#ECEBF8]' : ''
                            }`}
                        >
                            <p className="text-[16px] text-[#413F5E] font-semibold">
                                {student.first_name} {student.last_name}
                            </p>
                            <div className="flex-1" />
                            <Link to={`/analytics/student?id=${student.id}`}>
                                <div className="flex flex-row items-center gap-[6px] bg-[#D8D9E9] h-[30px] px-[15px] rounded-full">
                                    <p className="text-[#413F5E] font-bold text-[14px]">{t('Student analytics')}</p>
                                    <UserCardId />
                                </div>
                            </Link>
                            <div className="w-[24px]"></div>
                        </div>
                    ))}
                </div>
            )}
        </>
    )
}

const SingleYearButton = ({ year }) => {
    const { t } = useTranslation()
    return (
        <Link to={`/analytics/year?year=${encodeURIComponent(year.id)}`} className="h-[48px] bg-[#F8F8FB] rounded-[8px] px-[16px] flex items-center gap-[16px]">
            <p className="text-[16px] text-[#413F5E] font-semibold">{t('Year')} {year.id}</p>
            <div className="flex-1" />
            <CalendarIcon />
            <p className="text-[#413F5E] font-semibold text-[14px]">{t('Year analytics')}</p>
        </Link>
    )
}
